import React, { useMemo, useState } from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import { toast } from "sonner";
import {
  ArrowsClockwiseIcon,
  EnvelopeSimpleIcon,
  PlugIcon,
  ScanIcon,
  SparkleIcon,
  XCircleIcon,
} from "@phosphor-icons/react";
import { api } from "@/convex/_generated/api";
import { useLocalization } from "@/context/LocalizationContext";

const MAIL_PROVIDERS = [
  {
    id: "google",
    accent: "from-[#0F9D58] to-[#34A853]",
    ring: "shadow-[inset_0_0_0_1px_rgba(15,157,88,0.16)]",
  },
  {
    id: "microsoft",
    accent: "from-[#0078D4] to-[#005FB8]",
    ring: "shadow-[inset_0_0_0_1px_rgba(0,120,212,0.16)]",
    comingSoon: true,
  },
];

const IMPORT_POLICY_ITEMS = [
  {
    titleKey: "settings.imports.policy.startTitle",
    descriptionKey: "settings.imports.policy.startDescription",
    icon: PlugIcon,
  },
  {
    titleKey: "settings.imports.policy.signalsTitle",
    descriptionKey: "settings.imports.policy.signalsDescription",
    icon: ScanIcon,
  },
  {
    titleKey: "settings.imports.policy.confidentTitle",
    descriptionKey: "settings.imports.policy.confidentDescription",
    icon: SparkleIcon,
  },
];

const statusBadge = (provider, connection, t) => {
  if (provider.comingSoon) {
    return {
      label: t("settings.imports.states.comingSoon"),
      className:
        "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/70 dark:bg-amber-950/40 dark:text-amber-300",
    };
  }

  if (!connection || connection.status === "disconnected") {
    return {
      label: t("settings.imports.states.notConnected"),
      className:
        "border-zinc-300 bg-zinc-100 text-zinc-700 dark:border-zinc-600 dark:bg-zinc-700 dark:text-zinc-200",
    };
  }

  if (connection.status === "error") {
    return {
      label: t("settings.imports.states.needsAttention"),
      className:
        "border-red-200 bg-red-50 text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300",
    };
  }

  return {
    label: t("settings.imports.states.connected"),
    className:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300",
  };
};

const ProviderCard = ({
  provider,
  connection,
  connectingProvider,
  syncingProvider,
  disconnectingProvider,
  onConnect,
  onSync,
  onDisconnect,
}) => {
  const { t, formatDateTime } = useLocalization();
  const isConnected = connection && connection.status !== "disconnected";
  const isComingSoon = Boolean(provider.comingSoon);
  const isBusy =
    connectingProvider === provider.id ||
    syncingProvider === provider.id ||
    disconnectingProvider === provider.id;
  const badge = statusBadge(provider, connection, t);
  const providerLabel = t(`common.providers.${provider.id}`);

  return (
    <article
      className={`bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-none p-4 sm:p-6 hover:border-zinc-300 dark:hover:border-zinc-600 ${provider.ring}`}
    >
      <div className="flex flex-col gap-5">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-none bg-gradient-to-br ${provider.accent} text-white`}
              >
                <EnvelopeSimpleIcon size={18} weight="regular" />
              </div>
              <div>
                <h3 className="font-outfit text-xl tracking-tight text-zinc-900 dark:text-zinc-100">
                  {providerLabel}
                </h3>
              </div>
            </div>
            <p className="max-w-xl font-dmsans text-sm leading-6 text-zinc-600 dark:text-zinc-300">
              {t(`settings.imports.providers.${provider.id}.description`)}
            </p>
          </div>
          <span
            className={`shrink-0 border px-2.5 py-1 text-xs font-medium uppercase tracking-[0.15em] ${badge.className}`}
          >
            {badge.label}
          </span>
        </div>

        {!isComingSoon && (
          <>
            <div className="grid gap-3 text-sm sm:grid-cols-2">
              <div className="border border-zinc-200 bg-zinc-50 p-3 dark:border-zinc-700 dark:bg-zinc-900/40">
                <p className="mb-1 uppercase text-xs tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400">
                  {t("settings.imports.connectionFields.address")}
                </p>
                <p className="break-all font-dmsans text-zinc-900 dark:text-zinc-100">
                  {connection?.emailAddress || t("settings.imports.states.notConnected")}
                </p>
              </div>
              <div className="border border-zinc-200 bg-zinc-50 p-3 dark:border-zinc-700 dark:bg-zinc-900/40">
                <p className="mb-1 uppercase text-xs tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400">
                  {t("settings.imports.connectionFields.lastSync")}
                </p>
                <p className="font-dmsans text-zinc-900 dark:text-zinc-100">
                  {connection?.lastSyncedAt
                    ? formatDateTime(connection.lastSyncedAt, {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })
                    : t("settings.imports.lastSyncUnavailable")}
                </p>
              </div>
            </div>

            {connection?.status === "error" && connection.lastError && (
              <div className="border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300">
                {connection.lastError}
              </div>
            )}

            <div className="grid gap-2 sm:grid-cols-3">
              <button
                data-testid={`connect-${provider.id}-button`}
                onClick={() => onConnect(provider.id)}
                disabled={isBusy}
                className="flex min-h-11 items-center justify-center gap-2 bg-[#002FA7] px-4 py-2 text-sm font-medium text-white hover:bg-[#001B61] disabled:opacity-50"
              >
                <PlugIcon size={16} weight="regular" />
                {connectingProvider === provider.id
                  ? t("settings.imports.actions.redirecting")
                  : isConnected
                    ? t("settings.imports.actions.reconnect", {
                        provider: providerLabel,
                      })
                    : t("settings.imports.actions.connect", {
                        provider: providerLabel,
                      })}
              </button>
              <button
                data-testid={`sync-${provider.id}-button`}
                onClick={() => onSync(provider.id)}
                disabled={!isConnected || isBusy}
                className="flex min-h-11 items-center justify-center gap-2 border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-900 hover:bg-zinc-50 disabled:opacity-50 dark:border-zinc-600 dark:bg-zinc-700 dark:text-zinc-100 dark:hover:bg-zinc-600"
              >
                <ArrowsClockwiseIcon size={16} weight="regular" />
                {syncingProvider === provider.id
                  ? t("settings.imports.actions.syncing")
                  : t("settings.imports.actions.sync")}
              </button>
              <button
                data-testid={`disconnect-${provider.id}-button`}
                onClick={() => onDisconnect(provider.id)}
                disabled={!isConnected || isBusy}
                className="flex min-h-11 items-center justify-center gap-2 border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-900 hover:bg-zinc-50 disabled:opacity-50 dark:border-zinc-600 dark:bg-zinc-700 dark:text-zinc-100 dark:hover:bg-zinc-600"
              >
                <XCircleIcon size={16} weight="regular" />
                {disconnectingProvider === provider.id
                  ? t("settings.imports.actions.disconnecting")
                  : t("settings.imports.actions.disconnect")}
              </button>
            </div>
          </>
        )}
      </div>
    </article>
  );
};

const ImportsSettingsPage = () => {
  const { t } = useLocalization();
  const connections = useQuery(api.emailImports.listConnections);
  const disconnectProvider = useMutation(api.emailImports.disconnectProvider);
  const sendTestEmail = useAction(api.reminders.sendTestEmail);
  const startProviderConnection = useAction(
    api.emailSync.startProviderConnection,
  );
  const syncConnection = useAction(api.emailSync.syncConnection);
  const [testingEmail, setTestingEmail] = useState(false);
  const [connectingProvider, setConnectingProvider] = useState(null);
  const [syncingProvider, setSyncingProvider] = useState(null);
  const [disconnectingProvider, setDisconnectingProvider] = useState(null);

  const connectionsByProvider = useMemo(
    () =>
      (connections ?? []).reduce((map, connection) => {
        map[connection.provider] = connection;
        return map;
      }, {}),
    [connections],
  );

  const getProviderLabel = (provider) => t(`common.providers.${provider}`);

  const handleConnectProvider = async (provider) => {
    setConnectingProvider(provider);
    try {
      const response = await startProviderConnection({
        provider,
        originUrl: window.location.origin,
      });
      window.location.assign(response.url);
    } catch (error) {
      toast.error(
        error.message ||
          t("settings.imports.connectFailed", {
            provider: getProviderLabel(provider),
          }),
      );
      setConnectingProvider(null);
    }
  };

  const handleSyncProvider = async (provider) => {
    setSyncingProvider(provider);
    try {
      const result = await syncConnection({ provider });
      toast.success(
        t("settings.imports.syncComplete", {
          imported: result.imported,
          processed: result.processed,
        }),
      );
    } catch (error) {
      toast.error(
        error.message ||
          t("settings.imports.syncFailed", {
            provider: getProviderLabel(provider),
          }),
      );
    } finally {
      setSyncingProvider(null);
    }
  };

  const handleDisconnectProvider = async (provider) => {
    const providerLabel = getProviderLabel(provider);

    if (
      !window.confirm(
        t("settings.imports.disconnectConfirm", {
          provider: providerLabel,
        }),
      )
    ) {
      return;
    }

    setDisconnectingProvider(provider);
    try {
      await disconnectProvider({ provider });
      toast.success(
        t("settings.imports.disconnectSuccess", {
          provider: providerLabel,
        }),
      );
    } catch (error) {
      toast.error(error.message || t("settings.imports.disconnectFailed"));
    } finally {
      setDisconnectingProvider(null);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      await sendTestEmail({});
      toast.success(t("settings.imports.testEmailSuccess"));
    } catch (error) {
      toast.error(error.message || t("settings.imports.testEmailFailed"));
    } finally {
      setTestingEmail(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-none p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h3 className="font-outfit text-2xl tracking-tight text-zinc-900 dark:text-zinc-100">
              {t("settings.imports.title")}
            </h3>
            <p className="mt-2 font-dmsans text-sm leading-relaxed text-zinc-600 dark:text-zinc-300 max-w-2xl">
              {t("settings.imports.description")}
            </p>
          </div>
          <button
            data-testid="test-email-button"
            onClick={handleTestEmail}
            disabled={testingEmail}
            className="flex min-h-11 items-center justify-center gap-2 bg-[#002FA7] text-white rounded-none px-4 py-2 hover:bg-[#001B61] font-medium text-sm disabled:opacity-50"
          >
            <EnvelopeSimpleIcon size={16} weight="regular" />
            {testingEmail
              ? t("settings.imports.testingEmail")
              : t("settings.imports.testEmail")}
          </button>
        </div>
      </section>

      <div className="grid gap-4 2xl:grid-cols-2">
        {MAIL_PROVIDERS.map((provider) => (
          <ProviderCard
            key={provider.id}
            provider={provider}
            connection={connectionsByProvider[provider.id]}
            connectingProvider={connectingProvider}
            syncingProvider={syncingProvider}
            disconnectingProvider={disconnectingProvider}
            onConnect={handleConnectProvider}
            onSync={handleSyncProvider}
            onDisconnect={handleDisconnectProvider}
          />
        ))}
      </div>

      <section className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-none p-4 sm:p-6">
        <div className="flex flex-col gap-5">
          <div>
            <p className="uppercase text-xs tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 mb-2">
              {t("settings.imports.importPolicyEyebrow")}
            </p>
            <h3 className="font-outfit text-2xl tracking-tight text-zinc-900 dark:text-zinc-100">
              {t("settings.imports.importPolicyTitle")}
            </h3>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            {IMPORT_POLICY_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.titleKey}
                  className="border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-700 dark:bg-zinc-900/40"
                >
                  <div className="mb-3 flex h-10 w-10 items-center justify-center border border-[#002FA7]/15 bg-[#002FA7]/10 text-[#002FA7] dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-300">
                    <Icon size={18} weight="regular" />
                  </div>
                  <p className="font-dmsans text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    {t(item.titleKey)}
                  </p>
                  <p className="mt-2 font-dmsans text-sm leading-6 text-zinc-600 dark:text-zinc-300">
                    {t(item.descriptionKey)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ImportsSettingsPage;
